import { useCallback, useEffect, useState } from 'react'

/**
 * usePwaInstall — Maakt de app installeerbaar als PWA en stelt een hook beschikbaar
 * voor een eigen "installeer als app" UI.
 *
 * Wat het doet:
 * 1. Injecteert dynamisch een <link rel="manifest"> en <meta name="theme-color"> in <head>
 *    (we kunnen public/index.html niet bewerken, dus dit moet runtime).
 * 2. Registreert /sw.js als service worker (vereist voor Chrome's installability check).
 * 3. Vangt het `beforeinstallprompt`-event op en stelt `promptInstall()` beschikbaar.
 * 4. Onthoudt of de gebruiker de prompt heeft weggeklikt (localStorage).
 *
 * Werkt alleen op HTTPS (of localhost) — in de Magic Patterns sandbox-preview
 * worden geen install-prompts getoond. Op de gepubliceerde site werkt het wel.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const DISMISS_STORAGE_KEY = 'meester-djay:pwa-install-dismissed-at'
const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 14 // 14 dagen

function readDismissedRecently(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY)
    if (!raw) return false
    const timestamp = Number.parseInt(raw, 10)
    if (!Number.isFinite(timestamp)) return false
    return Date.now() - timestamp < DISMISS_COOLDOWN_MS
  } catch {
    return false
  }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // iOS Safari
  const iosStandalone = (
    window.navigator as Navigator & { standalone?: boolean }
  ).standalone
  if (iosStandalone) return true
  // Chrome / Edge / Android
  return window.matchMedia?.('(display-mode: standalone)').matches ?? false
}

function ensureManifestLink() {
  if (typeof document === 'undefined') return
  const existing = document.querySelector<HTMLLinkElement>(
    'link[rel="manifest"]',
  )
  if (existing) return
  const link = document.createElement('link')
  link.rel = 'manifest'
  link.href = '/manifest.json'
  document.head.appendChild(link)
}

function ensureThemeColorMeta() {
  if (typeof document === 'undefined') return
  const existing = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  )
  if (existing) return
  const meta = document.createElement('meta')
  meta.name = 'theme-color'
  meta.content = '#F97316'
  document.head.appendChild(meta)
}

function ensureAppleTouchMeta() {
  if (typeof document === 'undefined') return
  const apple = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-capable"]',
  )
  if (!apple) {
    const meta = document.createElement('meta')
    meta.name = 'apple-mobile-web-app-capable'
    meta.content = 'yes'
    document.head.appendChild(meta)
  }
  const title = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-title"]',
  )
  if (!title) {
    const meta = document.createElement('meta')
    meta.name = 'apple-mobile-web-app-title'
    meta.content = 'Meester Djay'
    document.head.appendChild(meta)
  }
}

async function registerServiceWorker() {
  if (typeof navigator === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  // Alleen registreren op HTTPS of localhost — anders failed het toch.
  const isSecure =
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  if (!isSecure) return
  try {
    await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch {
    // Stilletjes negeren — install prompt is nice-to-have, niet kritisch.
  }
}

export interface PwaInstallApi {
  /** True wanneer de browser een install-prompt heeft aangeboden en de gebruiker hem niet recent heeft weggeklikt. */
  canInstall: boolean
  /** True wanneer de app al als standalone draait (geïnstalleerd). */
  isInstalled: boolean
  /** Toont de native install-prompt. Resolved met true bij 'accepted'. */
  promptInstall: () => Promise<boolean>
  /** Verbergt de banner en onthoudt dit 14 dagen. */
  dismiss: () => void
}

export function usePwaInstall(): PwaInstallApi {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState<boolean>(() => isStandalone())
  const [dismissed, setDismissed] = useState<boolean>(() =>
    readDismissedRecently(),
  )

  // Eénmalig: manifest + meta-tags + service worker opzetten.
  useEffect(() => {
    ensureManifestLink()
    ensureThemeColorMeta()
    ensureAppleTouchMeta()
    void registerServiceWorker()
  }, [])

  // beforeinstallprompt opvangen.
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleBeforeInstall = (event: Event) => {
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }

    const handleInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled', handleInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleInstalled)
    }
  }, [])

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false
    try {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      setDeferredPrompt(null)
      if (choice.outcome === 'accepted') {
        setIsInstalled(true)
        return true
      }
      // Bij dismissed via native prompt: ook respecteren.
      try {
        window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()))
      } catch {
        /* noop */
      }
      setDismissed(true)
      return false
    } catch {
      return false
    }
  }, [deferredPrompt])

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()))
    } catch {
      /* noop */
    }
    setDismissed(true)
  }, [])

  const canInstall = Boolean(deferredPrompt) && !isInstalled && !dismissed

  return { canInstall, isInstalled, promptInstall, dismiss }
}
